/**
 * GET /api/training_cards/preview_template
 * POST /api/training_cards/apply_template
 *
 * Request fields match TrainingTemplateApplyContract::normalizeInput:
 * employee_id, training_template_id, initial_date, selections.
 * Client trainings[] / department_id are not a candidate source.
 */

import {
  computeTrainingSelectionCascade,
  normalizeMachineIds,
  normalizePositiveId,
  trainingIdsEqual,
} from './trainingSelectionCascade';
import { uiT } from './uiTranslate';

export const TRAINING_TEMPLATE_PREVIEW_PATH = '/api/training_cards/preview_template';
export const TRAINING_TEMPLATE_APPLY_PATH = '/api/training_cards/apply_template';
export const TRAINING_CARD_LEGACY_PATH = '/api/training_cards/card';

export const TEMPLATE_CANDIDATE_STATUSES = Object.freeze([
  'ready',
  'existing',
  'selection_required',
  'needs_configuration',
]);

export const TEMPLATE_PREVIEW_FAILED_KEY = 'TrainingCardAdd.Preview.Failed';
export const TEMPLATE_APPLY_VALIDATION_KEY = 'TrainingCardAdd.Apply.Validation';
export const TEMPLATE_APPLY_FAILED_KEY = 'TrainingCardAdd.Apply.Failed';
export const TEMPLATE_APPLY_SUCCESS_KEY = 'TrainingCardAdd.Apply.Success';

const UNSAFE_ERROR_RE = /sqlstate|\bsql\b|exception|stack trace|select |insert |update |delete |from |where |join |\.php\b|\/var\/|\/home\/|[a-z]:\\|\by:\\|\bw:\\|steelcon|at\s+\S+\s+\(/i;

const EMPTY_SESSION = Object.freeze({
  generation: 0,
  preview: null,
  error: null,
  selectedIds: [],
  drafts: {},
  manualUncheckedIds: [],
  loading: false,
});

function isPlainObject(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

export function isUnsafeTemplateErrorText(text) {
  if (typeof text !== 'string' || text.trim() === '') return true;
  return UNSAFE_ERROR_RE.test(text);
}

function findById(list, id) {
  const wanted = normalizePositiveId(id);
  if (wanted == null) return null;
  return (list || []).find((item) => trainingIdsEqual(item?.id, wanted)) || null;
}

export function emptyTemplatePreviewSession() {
  return { ...EMPTY_SESSION };
}

export function looksLikeTemplatePreviewPayload(value) {
  return isPlainObject(value) && Array.isArray(value.candidates);
}

function unwrapTemplatePreviewPayload(res) {
  if (!isPlainObject(res)) return null;
  if (looksLikeTemplatePreviewPayload(res.data)) return res.data;
  if (looksLikeTemplatePreviewPayload(res)) return res;
  return null;
}

export function groupTemplateCandidates(candidates) {
  const groups = {
    ready: [],
    existing: [],
    selection_required: [],
    needs_configuration: [],
  };
  for (const candidate of candidates || []) {
    const status = candidate?.status;
    if (groups[status]) groups[status].push(candidate);
  }
  return groups;
}

export function previewTrainingIds(preview) {
  const ids = [];
  const seen = new Set();
  for (const candidate of preview?.candidates || []) {
    const id = normalizePositiveId(candidate?.training_id);
    if (id == null || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

export function defaultSelectedTrainingIds(preview) {
  const fromList = (preview?.ready_training_ids || [])
    .map((id) => normalizePositiveId(id))
    .filter((id) => id != null);
  if (fromList.length) return fromList;
  return (preview?.candidates || [])
    .filter((candidate) => candidate?.status === 'ready')
    .map((candidate) => normalizePositiveId(candidate.training_id))
    .filter((id) => id != null);
}

export function candidateDraftFromPreview(candidate) {
  return {
    companyId: normalizePositiveId(candidate?.company_id),
    machineIds: normalizeMachineIds(candidate?.machine_ids),
    lecturerTypeId: normalizePositiveId(candidate?.lecturer_id),
    companyContactId: normalizePositiveId(candidate?.company_contact_id),
  };
}

export function initialDraftsFromCandidates(candidates) {
  const drafts = {};
  for (const candidate of candidates || []) {
    const id = normalizePositiveId(candidate?.training_id);
    if (id == null) continue;
    drafts[id] = candidateDraftFromPreview(candidate);
  }
  return drafts;
}

export function applyCandidateCascade(draft = {}, catalogs = {}) {
  const result = computeTrainingSelectionCascade({
    companyId: draft.companyId,
    machineIds: draft.machineIds,
    lecturerTypeId: draft.lecturerTypeId,
    companyContactId: draft.companyContactId,
    companies: catalogs.companies || [],
    machines: catalogs.machines || [],
    lecturerTypes: catalogs.lecturerTypes || [],
    companyContacts: catalogs.companyContacts || [],
  });
  return {
    companyId: result.companyId,
    machineIds: result.selectedMachineIds,
    lecturerTypeId: result.lecturerTypeId,
    companyContactId: result.companyContactId,
    availableMachines: result.availableMachines,
    availableLecturerTypes: result.availableLecturerTypes,
    availableContacts: result.availableContacts,
  };
}

export function resolvedCandidateAssignment(candidate, draft, catalogs) {
  const base = draft || candidateDraftFromPreview(candidate);
  const cascaded = catalogs ? applyCandidateCascade(base, catalogs) : base;
  return {
    company_id: normalizePositiveId(cascaded.companyId ?? cascaded.company_id),
    lecturer_id: normalizePositiveId(cascaded.lecturerTypeId ?? cascaded.lecturer_id),
    company_contact_id: normalizePositiveId(cascaded.companyContactId ?? cascaded.company_contact_id),
    machine_ids: normalizeMachineIds(cascaded.machineIds ?? cascaded.machine_ids),
  };
}

export function missingConfigurationFieldKeys(candidate, draft, catalogs) {
  const assignment = resolvedCandidateAssignment(candidate, draft, catalogs);
  const missing = [];
  if (assignment.company_id == null) missing.push('company');
  if (assignment.lecturer_id == null) missing.push('lecturer');
  if (assignment.company_contact_id == null) missing.push('contact');
  if (catalogs) {
    const cascaded = applyCandidateCascade(draft || candidateDraftFromPreview(candidate), catalogs);
    if ((cascaded.availableMachines || []).length > 0 && !assignment.machine_ids.length) {
      missing.push('machines');
    }
  } else if (!assignment.machine_ids.length) {
    missing.push('machines');
  }
  return missing;
}

export function isCandidateCheckable(candidate, draft, catalogs) {
  const status = candidate?.status;
  if (status === 'existing') return false;
  if (status === 'ready') return true;
  const assignment = resolvedCandidateAssignment(candidate, draft, catalogs);
  if (status === 'selection_required') {
    return assignment.company_contact_id != null;
  }
  if (status === 'needs_configuration') {
    return missingConfigurationFieldKeys(candidate, draft, catalogs).length === 0;
  }
  return false;
}

export function reconcileSelectedIds(selectedIds, candidates, drafts, catalogs) {
  const allowed = new Set(previewTrainingIds({ candidates }));
  const next = [];
  const seen = new Set();
  for (const raw of selectedIds || []) {
    const id = normalizePositiveId(raw);
    if (id == null || seen.has(id) || !allowed.has(id)) continue;
    const candidate = (candidates || []).find((row) => trainingIdsEqual(row?.training_id, id));
    if (!isCandidateCheckable(candidate, drafts?.[id], catalogs)) continue;
    seen.add(id);
    next.push(id);
  }
  return next;
}

function normalizedIdSet(ids) {
  const set = new Set();
  for (const raw of ids || []) {
    const id = normalizePositiveId(raw);
    if (id != null) set.add(id);
  }
  return set;
}

export function rememberManualUncheck(manualUncheckedIds, trainingId, checked) {
  const id = normalizePositiveId(trainingId);
  const next = [...normalizedIdSet(manualUncheckedIds)];
  if (id == null) return next;
  if (checked) return next.filter((item) => item !== id);
  if (next.includes(id)) return next;
  return [...next, id];
}

/**
 * Auto-check selection_required / needs_configuration once assignment is complete.
 * Ready stays default-checked from preview. Existing is never selected.
 * A manual uncheck for this generation suppresses later auto-check.
 */
export function autoCheckConfiguredCandidates(selectedIds, candidates, drafts, catalogs, manualUncheckedIds) {
  const kept = reconcileSelectedIds(selectedIds, candidates, drafts, catalogs);
  const selected = new Set(kept);
  const suppressed = normalizedIdSet(manualUncheckedIds);
  for (const candidate of candidates || []) {
    const status = candidate?.status;
    if (status !== 'selection_required' && status !== 'needs_configuration') continue;
    const id = normalizePositiveId(candidate?.training_id);
    if (id == null || suppressed.has(id) || selected.has(id)) continue;
    if (isCandidateCheckable(candidate, drafts?.[id], catalogs)) selected.add(id);
  }
  return [...selected];
}

export function toggleCandidateSelection(selectedIds, candidate, checked, drafts, catalogs, allCandidates) {
  const candidates = allCandidates?.length ? allCandidates : (candidate ? [candidate] : []);
  const id = normalizePositiveId(candidate?.training_id);
  const current = new Set(reconcileSelectedIds(selectedIds, candidates, drafts, catalogs));
  if (id == null) return [...current];
  if (checked) {
    if (isCandidateCheckable(candidate, drafts?.[id], catalogs)) current.add(id);
  } else {
    current.delete(id);
  }
  return [...current];
}

export function parseTemplatePreviewResponse(res) {
  if (!isPlainObject(res)) {
    return { ok: false, error: { kind: 'malformed', status: null } };
  }
  if (res.errors && isPlainObject(res.errors)) {
    return { ok: false, error: { kind: 'validation', status: 422 } };
  }
  const status = Number(res.status);
  const hasStatus = Number.isFinite(status);
  if (hasStatus && status !== 200) {
    const kind = status === 422 ? 'validation' : 'load_failed';
    return { ok: false, error: { kind, status } };
  }
  const data = unwrapTemplatePreviewPayload(res);
  if (!data) {
    return { ok: false, error: { kind: 'malformed', status: hasStatus ? status : null } };
  }
  return { ok: true, data };
}

export function parseTemplateApplyResponse(res) {
  if (!isPlainObject(res)) {
    return { ok: false, error: { kind: 'malformed', status: null } };
  }
  if (res.errors && isPlainObject(res.errors)) {
    return { ok: false, error: { kind: 'validation', status: 422 } };
  }
  const status = Number(res.status);
  const hasStatus = Number.isFinite(status);
  if (hasStatus && status === 422) {
    return { ok: false, error: { kind: 'validation', status: 422 } };
  }
  if (hasStatus && status >= 500) {
    return { ok: false, error: { kind: 'load_failed', status } };
  }
  if (hasStatus && status !== 201 && status !== 200) {
    const kind = status === 400 ? 'validation' : 'load_failed';
    return { ok: false, error: { kind, status } };
  }
  const applied = isPlainObject(res.applied) ? res.applied : (looksLikeTemplatePreviewPayload(res.data) ? res.data : null);
  if (!applied || !Array.isArray(applied.create_training_ids) || !Array.isArray(applied.skip_existing_training_ids)) {
    if (hasStatus && (status === 201 || status === 200) && Array.isArray(res.data)) {
      return {
        ok: true,
        data: res.data,
        applied: {
          create_training_ids: [],
          skip_existing_training_ids: [],
        },
      };
    }
    return { ok: false, error: { kind: 'malformed', status: hasStatus ? status : null } };
  }
  return { ok: true, data: res.data, applied };
}

export function templateApplyResultCounts(applied) {
  return {
    created: Array.isArray(applied?.create_training_ids) ? applied.create_training_ids.length : 0,
    skipped_existing: Array.isArray(applied?.skip_existing_training_ids) ? applied.skip_existing_training_ids.length : 0,
  };
}

export function resolveTemplateErrorMessage(error, t, validationKey, failedKey) {
  const key = error?.kind === 'validation' ? validationKey : failedKey;
  const fallback = error?.kind === 'validation'
    ? uiT('TrainingCardAdd.Apply.Validation')
    : uiT('Common.Toast.RequestFailedComplete');
  const translated = typeof t === 'function' ? t(key) : fallback;
  if (typeof translated !== 'string' || translated === key || isUnsafeTemplateErrorText(translated)) {
    return fallback;
  }
  return translated;
}

export function resolveTemplatePreviewErrorMessage(error, t) {
  return resolveTemplateErrorMessage(error, t, TEMPLATE_PREVIEW_FAILED_KEY, TEMPLATE_PREVIEW_FAILED_KEY);
}

export function resolveTemplateApplyErrorMessage(error, t) {
  return resolveTemplateErrorMessage(error, t, TEMPLATE_APPLY_VALIDATION_KEY, TEMPLATE_APPLY_FAILED_KEY);
}

export function previewMatchesForm(preview, values) {
  if (!preview || !values) return false;
  return trainingIdsEqual(preview.employee_id, values.employee_id)
    && trainingIdsEqual(preview.training_template_id, values.training_template_id);
}

export function buildTemplateApplyPayload({
  employeeId,
  trainingTemplateId,
  initialDate,
  selectedIds,
  preview,
  drafts,
  catalogs,
}) {
  const candidates = preview?.candidates || [];
  const allowed = new Set(previewTrainingIds(preview));
  const selections = {};
  for (const raw of selectedIds || []) {
    const id = normalizePositiveId(raw);
    if (id == null || !allowed.has(id)) continue;
    const candidate = candidates.find((row) => trainingIdsEqual(row?.training_id, id));
    if (!candidate || candidate.status === 'existing') continue;
    if (!isCandidateCheckable(candidate, drafts?.[id], catalogs)) continue;
    const assignment = resolvedCandidateAssignment(candidate, drafts?.[id], catalogs);
    if (assignment.company_contact_id == null) continue;
    selections[id] = {
      training_id: id,
      company_id: assignment.company_id,
      lecturer_id: assignment.lecturer_id,
      company_contact_id: assignment.company_contact_id,
      machine_ids: assignment.machine_ids,
    };
  }
  return {
    employee_id: normalizePositiveId(employeeId),
    training_template_id: normalizePositiveId(trainingTemplateId),
    initial_date: initialDate || null,
    selections,
  };
}

export function beginTemplateApply(isApplying) {
  if (isApplying) return { started: false, applying: true };
  return { started: true, applying: true };
}

export function departmentNameForCandidate(candidate, templateRecords) {
  const id = normalizePositiveId(candidate?.training_id);
  if (id == null) return candidate?.departmentName || '';
  if (candidate?.departmentName) return candidate.departmentName;
  const record = (templateRecords || []).find((row) => trainingIdsEqual(row?.training_id, id));
  const name = record?.training?.department?.name;
  return typeof name === 'string' ? name : '';
}

export function uniqueActiveTemplateRecords(template) {
  const records = Array.isArray(template?.template_records) ? template.template_records : [];
  const seen = new Set();
  const out = [];
  for (const rec of records) {
    const training = rec?.training;
    const tid = normalizePositiveId(rec?.training_id ?? training?.id);
    if (tid == null || seen.has(tid)) continue;
    if (training != null && training.validity != null && Number(training.validity) === 0) continue;
    seen.add(tid);
    out.push({
      training_id: tid,
      name: training?.name || rec?.name || '',
      departmentName: training?.department?.name || '',
      defined_id: training?.department?.defined_id ?? '',
    });
  }
  out.sort((a, b) => String(a.defined_id).localeCompare(String(b.defined_id), undefined, { numeric: true }));
  return out;
}

export function overlayTemplateRowsWithPreview(templateRows, preview) {
  const rows = templateRows || [];
  const candidates = preview?.candidates;
  if (!Array.isArray(candidates) || !candidates.length) {
    return rows.map((row) => ({
      training_id: row.training_id,
      name: row.name,
      status: null,
      departmentName: row.departmentName,
    }));
  }
  const byId = new Map();
  for (const candidate of candidates) {
    const id = normalizePositiveId(candidate?.training_id);
    if (id == null || byId.has(id)) continue;
    byId.set(id, candidate);
  }
  return rows.map((row) => {
    const candidate = byId.get(row.training_id);
    if (!candidate) {
      return {
        training_id: row.training_id,
        name: row.name,
        status: null,
        departmentName: row.departmentName,
      };
    }
    return {
      ...candidate,
      training_id: row.training_id,
      name: candidate.name || row.name,
      departmentName: row.departmentName,
    };
  });
}

export function catalogLabel(id, list, fallbackPrefix) {
  const normalized = normalizePositiveId(id);
  if (normalized == null) return '';
  const found = findById(list, normalized);
  if (found?.name) return found.name;
  if (found?.employee?.name) return found.employee.name;
  return fallbackPrefix ? `${fallbackPrefix} #${normalized}` : '';
}

export function machineLabels(ids, machines) {
  return normalizeMachineIds(ids)
    .map((id) => catalogLabel(id, machines, uiT('Common.MachineFallback')))
    .filter(Boolean);
}

export function contactLabel(id, contacts) {
  const normalized = normalizePositiveId(id);
  if (normalized == null) return '';
  const found = findById(contacts, normalized);
  return found?.employee?.name || found?.name || uiT('Common.TrainerFallback', { id: normalized });
}

export function nextTemplatePreviewSession(prev, event) {
  const current = prev && typeof prev === 'object' ? prev : emptyTemplatePreviewSession();
  if (!event || typeof event !== 'object') return current;

  if (event.type === 'invalidate') {
    return {
      generation: event.generation,
      preview: null,
      error: null,
      selectedIds: [],
      drafts: {},
      manualUncheckedIds: [],
      loading: !!event.shouldFetch,
    };
  }

  if (event.generation != null && event.generation !== current.generation) {
    return current;
  }

  if (event.type === 'rejected') {
    return {
      ...current,
      loading: false,
      preview: null,
      error: { kind: 'network' },
      selectedIds: [],
      drafts: {},
      manualUncheckedIds: [],
    };
  }

  if (event.type === 'response') {
    const parsed = parseTemplatePreviewResponse(event.response);
    if (!parsed.ok) {
      return {
        ...current,
        loading: false,
        preview: null,
        error: parsed.error,
        selectedIds: [],
        drafts: {},
        manualUncheckedIds: [],
      };
    }
    const drafts = initialDraftsFromCandidates(parsed.data.candidates);
    return {
      ...current,
      loading: false,
      preview: parsed.data,
      error: null,
      selectedIds: defaultSelectedTrainingIds(parsed.data),
      drafts,
      manualUncheckedIds: [],
    };
  }

  if (event.type === 'toggle') {
    const candidates = current.preview?.candidates || [];
    const candidate = candidates.find((row) => trainingIdsEqual(row?.training_id, event.trainingId));
    const manualUncheckedIds = rememberManualUncheck(
      current.manualUncheckedIds,
      event.trainingId,
      event.checked
    );
    if (!candidate) {
      const id = normalizePositiveId(event.trainingId);
      if (id == null) return current;
      const selected = new Set(current.selectedIds);
      if (event.checked) selected.add(id);
      else selected.delete(id);
      return { ...current, selectedIds: [...selected], manualUncheckedIds };
    }
    return {
      ...current,
      selectedIds: toggleCandidateSelection(
        current.selectedIds,
        candidate,
        event.checked,
        current.drafts,
        event.catalogs,
        candidates
      ),
      manualUncheckedIds,
    };
  }

  if (event.type === 'toggleAll') {
    if (!event.checked) {
      const suppressed = normalizedIdSet([
        ...(current.manualUncheckedIds || []),
        ...(current.selectedIds || []),
      ]);
      return { ...current, selectedIds: [], manualUncheckedIds: [...suppressed] };
    }
    const candidates = current.preview?.candidates || [];
    if (!candidates.length) {
      const selectedIds = (event.templateTrainingIds || [])
        .map((id) => normalizePositiveId(id))
        .filter((id) => id != null);
      const selected = new Set(selectedIds);
      return {
        ...current,
        selectedIds,
        manualUncheckedIds: [...normalizedIdSet(current.manualUncheckedIds)].filter((id) => !selected.has(id)),
      };
    }
    const selectedIds = candidates
      .filter((candidate) => isCandidateCheckable(
        candidate,
        current.drafts[normalizePositiveId(candidate.training_id)],
        event.catalogs
      ))
      .map((candidate) => normalizePositiveId(candidate.training_id))
      .filter((id) => id != null);
    const selected = new Set(selectedIds);
    return {
      ...current,
      selectedIds,
      manualUncheckedIds: [...normalizedIdSet(current.manualUncheckedIds)].filter((id) => !selected.has(id)),
    };
  }

  if (event.type === 'cascade') {
    const trainingId = normalizePositiveId(event.trainingId);
    const candidates = current.preview?.candidates || [];
    const candidate = candidates.find((row) => trainingIdsEqual(row?.training_id, trainingId));
    if (trainingId == null || !candidate) return current;
    const previous = current.drafts[trainingId] || candidateDraftFromPreview(candidate);
    const nextInput = {
      companyId: event.field === 'companyId' ? event.value : previous.companyId,
      machineIds: event.field === 'machineIds' ? event.value : previous.machineIds,
      lecturerTypeId: event.field === 'lecturerTypeId' ? event.value : previous.lecturerTypeId,
      companyContactId: event.field === 'companyContactId' ? event.value : previous.companyContactId,
    };
    const cascaded = applyCandidateCascade(nextInput, event.catalogs || {});
    const drafts = {
      ...current.drafts,
      [trainingId]: {
        companyId: cascaded.companyId,
        machineIds: cascaded.machineIds,
        lecturerTypeId: cascaded.lecturerTypeId,
        companyContactId: cascaded.companyContactId,
      },
    };
    return {
      ...current,
      drafts,
      selectedIds: autoCheckConfiguredCandidates(
        current.selectedIds,
        candidates,
        drafts,
        event.catalogs,
        current.manualUncheckedIds
      ),
    };
  }

  return current;
}
